import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * オンライン面会設定 API 操作クラス
 */
export default class OnlineReservationSettings {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET OnlineReservationSettings
   */
  public async get(userStore: LoginUser): Promise<OnlineReservationSettingsGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OnlineReservationSettings, formatParams);
    const response = await this.commonService.executeGet<OnlineReservationSettingsGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OnlineReservationSettings
   */
  public async patch(userStore: LoginUser, onlineReservationSetting: OnlineReservationSettingsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OnlineReservationSettings, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, onlineReservationSetting, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OnlineReservationSettingsGetModel {
  /** 施設を特定するためのID */
  facilityId: number;

  /** 予約時間単位 */
  unit: ReservationUnit;
  /** 家族ユーザーの予約可能日 */
  availableDate: AvailableDate;
  /** 予約枠あたりの予約可能数 */
  reservationPerCounts: ReservationPerCounts;
  /** 予約枠あたりの予約数カウント方法 */
  countType: CountType;
  /** 利用者ごとの予約間隔制限 */
  intervalLimit: IntervalLimit;
  /** 利用者ごとの同月内の予約可能な面会数 */
  visitsLimitOfMonth: VisitsLimitOfMonth;
  /** 利用者ごとの同時に予約可能な面会数 */
  concurrentNumberLimit: ConcurrentNumberLimit;
  /** 利用者ごとの同時に予約可能な面会数の家族ユーザー表示 */
  showConcurrentNumberLimit: boolean;
  /** 利用者ごとの予約制限の確認範囲 */
  limitType: LimitType;
  /** 面会予約の受付中もしくは停止中 */
  canReservation: boolean;
  /** 利用者グループの使用有無設定 */
  groupUseType: GroupUseType;
  /** 家族ユーザーへの面会スケジュールの公開予約機能を使用するか */
  usePublishSchedule: boolean;
  /** 面会スケジュールの公開予約日時（前月の〇日の〇時） */
  publishDatetime: PublishDatetime | null;
  /** 自動承諾を使用するか否か */
  autoApproval: boolean;
  /** 家族ユーザーへの案内 */
  description: string | null;
  /** 家族からの連絡事項の使用可否 */
  useText: boolean;

  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;
}

export enum ReservationUnit {
  /** 15分 */
  FIFTEEN_MINUTES = 15,
  /** 30分 */
  THIRTY_MINUTES = 30,
  /** 1時間 */
  ONE_HOUR = 60
}

export enum AvailableDate {
  /** 当日から */
  TODAY = 0,
  /** 翌日から */
  NEXT_DAY = 1,
  /** 3日後から */
  THREE_DAYS_LATER = 3,
  /** 5日後から */
  FICE_DAYS_LATER = 5,
  /** 1週間後から */
  ONE_WEEK_LATER = 7
}

export enum ReservationPerCounts {
  /** 無制限 */
  UNLIMITED = 0,
  /** 1件 */
  ONE = 1,
  /** 2件 */
  TWO = 2,
  /** 3件 */
  THREE = 3,
  /** 4件 */
  FOUR = 4,
  /** 5件 */
  FIVE = 5,
  /** 6件 */
  SIX = 6,
  /** 7件 */
  SEVEN = 7,
  /** 8件 */
  EIGHT = 8,
  /** 9件 */
  NINE = 9,
  /** 10件 */
  TEN = 10
}

export enum CountType {
  FACILITY = 0,
  GROUP = 1
}

export enum LimitType {
  UNKNOWN = -1,
  ALL = 0,
  SEPARATE = 1
}

export enum IntervalLimit {
  /** 制限無し */
  NO_LIMIT = 0,
  /** 1日 */
  ONE_DATE = 1,
  /** 3日 */
  THREE_DATE = 3,
  /** 1週間 */
  ONE_WEEK = 7,
  /** 2週間 */
  TWO_WEEK = 14,
  /** 3週間 */
  THREE_WEEK = 21,
  /** 4週間 */
  FOUR_WEEK = 28,
  /** 5週間 */
  FIVE_WEEK = 35
}

export enum VisitsLimitOfMonth {
  /** 制限無し */
  NO_LIMIT = 0,
  /** 1件 */
  ONE = 1,
  /** 2件 */
  TWO = 2,
  /** 3件 */
  THREE = 3,
  /** 4件 */
  FOUR = 4,
  /** 5件 */
  FIVE = 5
}

export enum ConcurrentNumberLimit {
  /** 制限無し */
  NO_LIMIT = 0,
  /** 1件 */
  ONE = 1,
  /** 2件 */
  TWO = 2,
  /** 3件 */
  THREE = 3,
  /** 4件 */
  FOUR = 4,
  /** 5件 */
  FIVE = 5
}

/**
 * ### 利用者グループの使用有無設定
 */
export enum GroupUseType {
  DISUSE = 0,
  USE = 1
}

export interface PublishDatetime {
  /** 日 */
  date: number;
  /** 時 */
  hour: number;
}

export interface OnlineReservationSettingsPatchModel {
  /** 予約時間単位 */
  unit: ReservationUnit;
  /** 家族ユーザーの予約可能日 */
  availableDate: AvailableDate;
  /** 予約枠あたりの予約可能数 */
  reservationPerCounts: ReservationPerCounts;
  /** 予約枠あたりの予約数カウント方法 */
  countType: CountType;
  /** 利用者ごとの予約間隔制限 */
  intervalLimit: IntervalLimit;
  /** 利用者ごとの同月内の予約可能な面会数 */
  visitsLimitOfMonth: VisitsLimitOfMonth;
  /** 利用者ごとの同時に予約可能な面会数 */
  concurrentNumberLimit: ConcurrentNumberLimit;
  /** 利用者ごとの同時に予約可能な面会数の家族ユーザー表示 */
  showConcurrentNumberLimit: boolean;
  /** 利用者ごとの予約制限の確認範囲 */
  limitType: LimitType;
  /** 面会予約の受付中もしくは停止中 */
  canReservation: boolean;
  /** 利用者グループの使用有無設定 */
  groupUseType: GroupUseType;
  /** 家族ユーザーへの面会スケジュールの公開予約機能を使用するか */
  usePublishSchedule: boolean;
  /** 面会スケジュールの公開予約日時（前月の〇日の〇時） */
  publishDatetime: PublishDatetime | null;
  /** 自動承諾を使用するか否か */
  autoApproval: boolean;
  /** 家族ユーザーへの案内 */
  description: string | null;
  /** 家族からの連絡事項の使用可否 */
  useText: boolean;
  /** 更新日時 */
  updated: number;
}
